import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsAPI, scenariosAPI, instructorStudentsAPI, coursesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Play, 
  Clock, 
  ChartLine, 
  Users, 
  Stethoscope,
  CheckCircle,
  Spinner,
  Warning,
  TrendUp,
  TrendDown,
  Heartbeat,
  GraduationCap,
  Eye,
  BookOpen,
  Target,
  Star,
  WarningCircle
} from '@phosphor-icons/react';
import { Progress } from '../components/ui/progress';

// Dashboard para Estudiantes/Alumnos
const StudentDashboard = ({ stats, scenarios, navigate }) => {
  const progressPercentage = stats?.completed_simulations && stats?.my_simulations 
    ? Math.round((stats.completed_simulations / Math.max(stats.my_simulations, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Mis Simulaciones</p>
                <p className="text-3xl font-bold">{stats?.my_simulations || 0}</p>
              </div>
              <ChartLine size={40} className="text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Completadas</p>
                <p className="text-3xl font-bold">{stats?.completed_simulations || 0}</p>
              </div>
              <CheckCircle size={40} className="text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Promedio</p>
                <p className="text-3xl font-bold">{stats?.average_score || 0}%</p>
              </div>
              <Star size={40} className="text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Escenarios</p>
                <p className="text-3xl font-bold">{scenarios?.length || 0}</p>
              </div>
              <Stethoscope size={40} className="text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progreso */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target size={20} />
            Mi Progreso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between text-sm">
              <span>Simulaciones completadas</span>
              <span className="font-medium">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3" />
            <p className="text-sm text-gray-500">
              {stats?.completed_simulations || 0} de {stats?.my_simulations || 0} simulaciones finalizadas
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Escenarios Recomendados */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Escenarios para ti</h2>
          <Button variant="ghost" onClick={() => navigate('/app/scenarios')}>
            Ver todos
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios?.slice(0, 6).map((scenario) => (
            <Card 
              key={scenario.id} 
              className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-blue-500"
              onClick={() => navigate(`/scenarios/${scenario.id}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base">{scenario.nombre}</CardTitle>
                  <Badge variant={scenario.nivel === 'alta' ? 'destructive' : scenario.nivel === 'media' ? 'default' : 'secondary'}>
                    {scenario.nivel}
                  </Badge>
                </div>
                <CardDescription>
                  {scenario.especialidad?.replace('_', ' ')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 line-clamp-2">{scenario.briefing?.substring(0, 100)}...</p>
                <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                  <Clock size={14} />
                  <span>{Math.floor((scenario.tiempo_maximo || 600) / 60)} min</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Dashboard para Instructores
const InstructorDashboard = ({ stats, navigate, instructorMetrics, activeSessions }) => {
  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Mis Estudiantes</p>
                <p className="text-3xl font-bold">{instructorMetrics?.total_estudiantes || 0}</p>
              </div>
              <Users size={40} className="text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Sesiones Activas</p>
                <p className="text-3xl font-bold">{instructorMetrics?.simulaciones_activas_ahora || 0}</p>
                {instructorMetrics?.simulaciones_activas_ahora > 0 && (
                  <p className="text-xs text-green-200 animate-pulse">En vivo ahora</p>
                )}
              </div>
              <Heartbeat size={40} className="text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Cursos Activos</p>
                <p className="text-3xl font-bold">{instructorMetrics?.cursos_activos || 0}</p>
              </div>
              <GraduationCap size={40} className="text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Promedio Estudiantes</p>
                <p className="text-3xl font-bold">{instructorMetrics?.promedio_puntaje_estudiantes || 0}%</p>
              </div>
              <TrendUp size={40} className="text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertas de estudiantes con bajo rendimiento */}
      {instructorMetrics?.estudiantes_con_alerta > 0 && (
        <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <WarningCircle className="text-yellow-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-800">
                  {instructorMetrics.estudiantes_con_alerta} estudiantes necesitan atención
                </h3>
                <p className="text-sm text-yellow-700">
                  Tienen un promedio inferior al 60% en sus evaluaciones
                </p>
              </div>
              <Button 
                variant="outline" 
                className="ml-auto border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                onClick={() => navigate('/app/instructor/my-students')}
              >
                Ver estudiantes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Acciones rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => navigate('/app/instructor/supervision')}
        >
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Eye size={24} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Supervisión en Vivo</h3>
              <p className="text-sm text-gray-500">Monitorea simulaciones activas</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => navigate('/app/instructor/my-students')}
        >
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <Users size={24} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Mis Estudiantes</h3>
              <p className="text-sm text-gray-500">Progreso y métricas</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => navigate('/app/instructor/courses')}
        >
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <BookOpen size={24} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Gestión de Cursos</h3>
              <p className="text-sm text-gray-500">Administra tus cursos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sesiones activas */}
      {activeSessions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heartbeat size={20} className="text-green-500" />
              Simulaciones en Curso
              <Badge className="bg-green-500 animate-pulse">{activeSessions.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeSessions.slice(0, 5).map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {session.student?.nombre?.charAt(0)}{session.student?.apellido?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{session.student?.nombre} {session.student?.apellido}</p>
                      <p className="text-sm text-gray-500">{session.scenario?.nombre || 'Escenario'}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate('/app/instructor/supervision')}
                  >
                    <Eye size={16} className="mr-1" />
                    Supervisar
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Dashboard para Admin Institucional
const AdminDashboard = ({ stats, navigate }) => {
  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Total Usuarios</p>
                <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
              </div>
              <Users size={40} className="text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm">Instructores</p>
                <p className="text-3xl font-bold">{stats?.total_instructors || 0}</p>
              </div>
              <GraduationCap size={40} className="text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm">Alumnos</p>
                <p className="text-3xl font-bold">{stats?.total_students || 0}</p>
              </div>
              <Users size={40} className="text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Simulaciones Mes</p>
                <p className="text-3xl font-bold">{stats?.monthly_simulations || 0}</p>
              </div>
              <ChartLine size={40} className="text-purple-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumen institucional */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Uso de la Plataforma</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Escenarios disponibles</span>
                <span className="font-bold">{stats?.total_scenarios || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total simulaciones</span>
                <span className="font-bold">{stats?.total_simulations || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Cursos activos</span>
                <span className="font-bold">{stats?.active_courses || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Promedio institucional</span>
                <span className="font-bold">{stats?.institutional_average || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/app/admin/users')}>
              <Users size={18} className="mr-2" />
              Gestionar Usuarios
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/app/admin/scenarios')}>
              <Stethoscope size={18} className="mr-2" />
              Gestionar Escenarios
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/app/instructor/courses')}>
              <BookOpen size={18} className="mr-2" />
              Gestionar Cursos
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/app/admin/roles')}>
              <GraduationCap size={18} className="mr-2" />
              Roles y Permisos
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isInstructor } = useAuth();
  const [stats, setStats] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [instructorMetrics, setInstructorMetrics] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const isStudent = user?.role === 'alumno';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Cargar stats básicas y escenarios para todos
      const [statsRes, scenariosRes] = await Promise.all([
        statsAPI.getDashboard(),
        scenariosAPI.getAll()
      ]);
      
      setStats(statsRes.data);
      setScenarios(scenariosRes.data);

      // Cargar métricas de instructor si corresponde
      if (isInstructor || isAdmin) {
        try {
          const [metricsRes, sessionsRes] = await Promise.all([
            instructorStudentsAPI.getDashboardMetrics(),
            instructorStudentsAPI.getActiveSessions({})
          ]);
          setInstructorMetrics(metricsRes.data);
          setActiveSessions(sessionsRes.data.sessions || []);
        } catch (error) {
          console.error('Error loading instructor data:', error);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [isInstructor, isAdmin]);

  useEffect(() => {
    loadData();
    
    // Refrescar datos cada 60 segundos para instructores
    if (isInstructor || isAdmin) {
      const interval = setInterval(loadData, 60000);
      return () => clearInterval(interval);
    }
  }, [loadData, isInstructor, isAdmin]);

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  const getRoleLabel = () => {
    if (user?.role === 'super_admin') return 'Super Administrador';
    if (isAdmin) return 'Administrador Institucional';
    if (isInstructor) return 'Instructor';
    return 'Alumno';
  };

  return (
    <>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="dashboard-title">
              Bienvenido, {user?.nombre}
            </h1>
            <div className="text-slate-500 mt-1 flex items-center gap-2">
              <Badge variant="outline">{getRoleLabel()}</Badge>
              {isInstructor && instructorMetrics?.simulaciones_activas_ahora > 0 && (
                <Badge className="bg-green-500 animate-pulse">
                  {instructorMetrics.simulaciones_activas_ahora} en vivo
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex gap-3">
            {(isInstructor || isAdmin) && (
              <Button 
                variant="outline" 
                onClick={() => navigate('/app/instructor/supervision')}
              >
                <Eye size={18} className="mr-2" />
                Supervisión
              </Button>
            )}
            <Button onClick={() => navigate('/app/scenarios')} data-testid="new-simulation-btn">
              <Play size={18} className="mr-2" />
              Nueva Simulación
            </Button>
          </div>
        </div>

        {/* Dashboard específico por rol */}
        {isStudent ? (
          <StudentDashboard stats={stats} scenarios={scenarios} navigate={navigate} />
        ) : isInstructor && !isAdmin ? (
          <InstructorDashboard 
            stats={stats} 
            navigate={navigate} 
            instructorMetrics={instructorMetrics}
            activeSessions={activeSessions}
          />
        ) : (
          <AdminDashboard stats={stats} navigate={navigate} />
        )}
      </div>
    </>
  );
}
